import React from 'react'
import { observer } from 'mobx-react-lite'
import { MDBCard, MDBCardBody, MDBProgress } from 'mdb-react-ui-kit'
import LoadingSpinner from './../../../../components/shared/LoadingSpinner.component'
import { getHeadshot } from './../../../../shared/utilities/getHeadshot.function'
import {
  getRaceKeyFromMilestone,
  getRaceThresholdForMilestone,
} from './../../../../constants/race-to-the-ring.constants'
import Store from './../../store'

import './UpcomingRaceWinners.component.scss'

const DEFAULT_LIMIT = 2
const DEFAULT_RACE_KEY = 'agent'
const SHOW_ORDINAL = false

const getNumber = value => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const getMilestoneValue = (milestone, key) => {
  if (!milestone) return undefined
  if (typeof milestone.get === 'function') {
    const value = milestone.get(key)
    if (typeof value !== 'undefined') return value
  }
  if (typeof milestone.raw === 'function') {
    const raw = milestone.raw()
    if (raw && Object.prototype.hasOwnProperty.call(raw, key)) return raw[key]
  }
  if (typeof milestone === 'object' && milestone !== null) {
    return milestone[key]
  }
  return undefined
}

const getRacerName = milestone => {
  const first = getMilestoneValue(milestone, 'mi_fname') ?? ''
  const last = getMilestoneValue(milestone, 'mi_lname') ?? ''
  const name = `${first} ${last}`.trim()
  return name || 'TBD'
}

const getRacerHeadshot = milestone => {
  const userId = getMilestoneValue(milestone, 'user_id')
  const user = Store.getUserById(userId)
  const userPicture =
    (typeof user?.get === 'function' && user.get('u_picture')) ??
    (typeof user?.raw === 'function' ? user.raw()?.u_picture : undefined)
  const milestonePicture =
    getMilestoneValue(milestone, 'u_picture') ??
    getMilestoneValue(milestone, 'pic') ??
    getMilestoneValue(milestone, 'image')

  return getHeadshot({
    u_picture: userPicture ?? milestonePicture,
    id: userId,
  })
}

const formatPoints = value => `${getNumber(value).toLocaleString()} pts`

const getOrdinal = position => {
  if (SHOW_ORDINAL) {
    const remainder10 = position % 10
    const remainder100 = position % 100

    if (remainder10 === 1 && remainder100 !== 11) return `${position}st Place`
    if (remainder10 === 2 && remainder100 !== 12) return `${position}nd Place`
    if (remainder10 === 3 && remainder100 !== 13) return `${position}rd Place`
    return `${position}th Place`
  }
}

const getPointsToGoal = (milestone, value) => {
  const goal = getRaceThresholdForMilestone(milestone)
  return {
    goal,
    remaining: Math.max(goal - value, 0),
  }
}

const getGoalProgress = (value, goal) => (goal ? Math.min((value / goal) * 100, 100) : 0)

const formatRaceLabel = raceKey => {
  if (!raceKey) return 'Agent'
  return raceKey
    .split(/[\s-_]/)
    .filter(Boolean)
    .map(segment => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ')
}

const UpcomingRaceWinners = ({ limit = DEFAULT_LIMIT, raceKey = DEFAULT_RACE_KEY }) => {
  const racers = Array.isArray(Store?.Racers?.[raceKey]) ? Store.Racers[raceKey] : []
  const contenders = racers
    .slice()
    .sort((a, b) => getNumber(b?.get?.('mi_value')) - getNumber(a?.get?.('mi_value')))
    .slice(0, Math.max(1, limit))

  if (Store.isLoading) {
    return (
      <div id='UpcomingRaceWinnersComponent'>
        <LoadingSpinner size='md' isActive={true} label='Loading Race to the Ring data' />
      </div>
    )
  }

  if (!contenders.length) {
    return (
      <section id='UpcomingRaceWinnersComponent' className='race-panel'>
        <h1 className='race-panel__finalists'>Who will win the next Royal Ring?</h1>
        <p className='race-panel__empty'>We'll highlight the next contenders as soon as data is available.</p>
      </section>
    )
  }

  const leaderValue = getNumber(contenders[0]?.get?.('mi_value'))

  return (
    <section id='UpcomingRaceWinnersComponent' className='race-panel'>
      <h1 className='race-panel__finalists'>Who will win the next Royal Ring?</h1>

      <div className='race-panel__cards'>
        {contenders.map((racer, index) => {
          const value = getNumber(racer?.get?.('mi_value'))
          const { goal, remaining } = getPointsToGoal(racer, value)
          const pointsBehindLeader = index === 0 ? 0 : Math.max(leaderValue - value, 0)
          const raceLabel = formatRaceLabel(getRaceKeyFromMilestone(racer))

          return (
            <MDBCard
              key={racer?.get?.('milestone_id') ?? `contender-${index}`}
              className={`race-panel__card ${index === 0 ? 'race-panel__card--leader' : ''}`}
            >
              <MDBCardBody>
                <div className='race-panel__rank'>{getOrdinal(index + 1)}</div>

                <div className='race-panel__identity'>
                  <img src={getRacerHeadshot(racer)} alt={getRacerName(racer)} className='race-panel__avatar' />
                  <div>
                    <p className='race-panel__name'>{getRacerName(racer)}</p>
                    <p className='race-panel__goal'>
                      {raceLabel} Race · Goal {formatPoints(goal)}
                    </p>
                    <p className='race-panel__gap'>{formatPoints(remaining)} to goal</p>
                    {index !== 0 ? (
                      <p className='race-panel__gap race-panel__gap--secondary'>
                        {formatPoints(pointsBehindLeader)} behind leader
                      </p>
                    ) : null}
                  </div>
                </div>

                <div className='race-panel__stats'>
                  <div className='race-panel__metrics'>
                    <div className='race-panel__metric'>
                      <span className='race-panel__label'>Current Points</span>
                      <strong className='race-panel__value'>{formatPoints(value)}</strong>
                    </div>
                    <div className='race-panel__metric'>
                      <span className='race-panel__label'>Points to Goal</span>
                      <strong className='race-panel__value'>{formatPoints(remaining)}</strong>
                    </div>
                    {index !== 0 ? (
                      <div className='race-panel__metric'>
                        <span className='race-panel__label'>Behind Leader</span>
                        <strong className='race-panel__value'>{formatPoints(pointsBehindLeader)}</strong>
                      </div>
                    ) : null}
                  </div>
                  <MDBProgress material color={index === 0 ? 'success' : 'info'} value={getGoalProgress(value, goal)} />
                </div>
              </MDBCardBody>
            </MDBCard>
          )
        })}
      </div>
    </section>
  )
}

export default observer(UpcomingRaceWinners)
